import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { countConditions } from "@estelle/domain";
import type { DirectoryEntry, Filter } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { PhoneNumber } from "@/components/phone/PhoneNumber";
import {
  LoadFailed,
  NoAccess,
  NoMatches,
  NothingYet,
} from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { Fab } from "@/components/shell/Fab";
import { SectionAction } from "@/components/shell/SectionAction";
import { renderCell } from "@/components/table/cells";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { SelectionBar } from "@/components/table/SelectionBar";
import {
  SELECT_ALL_CAP,
  inViewOf,
  selectionFacts,
  useSelection,
} from "@/components/table/selection";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { avatarTint, initials } from "@/lib/initials";
import { reportingNow } from "@/lib/reportClock";
import { cn } from "@/lib/utils";
import { DirectoryBulkActions } from "@/modules/contacts/directory/screens/DirectoryBulkActions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { figuresLine } from "@/modules/contacts/directory/format";
import { DIRECTORY_KEYS } from "@/modules/contacts/directory/keys";
import {
  parseRecordKey,
  projectDirectoryEntry,
  recordKey,
} from "@/modules/contacts/directory/projections";
import { CUSTOMER_KEYS } from "@/modules/contacts/customers/useCustomerAnnotations";
import { CustomerDetail } from "@/modules/contacts/customers/screens/CustomerDetail";
import { ContactDetail } from "@/modules/contacts/screens/ContactDetail";
import { ContactForm } from "@/modules/contacts/screens/ContactForm";
import { withVisibleKinds } from "@/modules/contacts/standing";
import { typeNamesOf } from "@/modules/contacts/typeNames";
import type { SectionScreenProps } from "@/modules/types";
import { useOpenRecord } from "@/routes/useOpenRecord";

const KIND_LABEL = { contact: "Contact", customer: "Customer" } as const;

/**
 * The name with its initials (#637), and a "Contact" mark on the hand-kept
 * ones: Kind starts hidden because nearly every row is a customer, so the
 * exception is what the cell says.
 */
function NameCell({ entry }: { entry: DirectoryEntry }) {
  const name = entry.displayName || "—";
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span
        aria-hidden
        className={cn(
          "flex size-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold",
          avatarTint(recordKey(entry)),
        )}
      >
        {initials(entry.displayName || "?")}
      </span>
      <span className="min-w-0 truncate font-medium">{name}</span>
      {entry.kind === "contact" ? (
        <Badge variant="outline" className="shrink-0 font-normal">
          Contact
        </Badge>
      ) : null}
    </span>
  );
}

/**
 * One entry as a card on a phone: the initials and name on the first line,
 * a customer's orders and spend or a contact's company on the second, where
 * they are on the third, how to reach them below. The generic label/value
 * stack would put twelve rows under every name, most of them dashes for one
 * kind or the other.
 */
function DirectoryMobileCard({ entry }: { entry: DirectoryEntry }) {
  const place = [entry.city, entry.state].filter(Boolean).join(", ");
  const about =
    entry.kind === "contact" ? entry.companyName : figuresLine(entry);
  return (
    <div className="min-w-0">
      <NameCell entry={entry} />
      {about ? (
        <div className="mt-1 truncate text-sm tabular-nums">{about}</div>
      ) : null}
      {place ? (
        <div className="text-muted-foreground mt-0.5 truncate text-sm">
          {place}
        </div>
      ) : null}
      {entry.phone ? (
        <div className="text-muted-foreground mt-1 text-sm">
          <PhoneNumber number={entry.phone} name={entry.displayName} />
        </div>
      ) : null}
      {entry.isArchived || entry.tags.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {entry.isArchived ? (
            <Badge variant="outline" className="font-normal">
              Archived
            </Badge>
          ) : null}
          {entry.tags.slice(0, 4).map((tag) => (
            <Badge key={tag} variant="outline" className="font-normal">
              {tag}
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * All Contacts (#493): the contacts the team keeps by hand and Shopify's
 * customers, in one list.
 *
 * Filter, sort, search, columns, paging and export are the shared ones — this
 * screen only says what a row is and what opens it. A contact opens the contact
 * drawer, with everything it has always had; a customer opens the customer
 * drawer the Customers section opens, where the team's note and tags are the
 * only things that can change — Shopify's record is read-only. Which one is open
 * lives in the URL (`?record=`), so a link opens it cold.
 *
 * What the list holds is the server's to decide: customers only for a holder of
 * `customers.view`, their phone and email only for `customers.view_contact`. The
 * two checks below are a courtesy for the demo build, whose fixture adapter does
 * not know who is asking — against the API they change nothing, because the rows
 * were never sent.
 */
export function DirectoryList({
  fields,
  filter,
  search,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();
  const permissions = usePermissions();
  const canViewCustomers = can(permissions, "customers.view");
  const canViewCustomerContact = can(permissions, "customers.view_contact");

  const [limit, setLimit] = useState(25);
  /** Paging stays local, as in every list — see `OrdersList`. */
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];
  const { recordId, open } = useOpenRecord();
  const [creating, setCreating] = useState(false);
  /** The ticked contacts and customers (#634), kept across pages and filters. */
  const selection = useSelection<DirectoryEntry>(recordKey);

  /*
    Customers are left out for someone who may not view them, as a standing
    condition ANDed with their own filter rather than replacing it — the way a
    section is a query, not a screen (`contactsList`'s type filter).
  */
  const effective: Filter = withVisibleKinds(canViewCustomers, filter);

  const query = useQuery({
    queryKey: [...DIRECTORY_KEYS.list, effective, search, sort, limit, cursor],
    queryFn: () =>
      data.contacts.directory({
        filter: effective,
        search,
        sort,
        limit,
        cursor,
      }),
  });

  /*
    The contact types, for the Type column's names (#514): a row holds ids. The
    query key is the one every contact screen shares, so this is the request
    `ContactForm` makes and not a second one.
  */
  const typesQuery = useQuery({
    queryKey: ["contacts", "types"],
    queryFn: () => data.contacts.listTypes(),
  });
  const showsTypes = fields.some((field) => field.name === "typeIds");

  const opened = recordId === null ? null : parseRecordKey(recordId);

  /*
    A customer opened cold — from a shared URL — is not on this page of the
    list, so the drawer's title comes from the customer itself. The same query,
    under the same key, `CustomerDetail` makes: one request, not two.
  */
  const openCustomer = useQuery({
    queryKey: CUSTOMER_KEYS.detail(opened?.id ?? ""),
    queryFn: () => data.customers.get(opened?.id ?? ""),
    enabled: opened?.kind === "customer" && canViewCustomers,
  });

  /**
   * The entry as the caller may see it. A customer's phone and email are left
   * off for someone without `customers.view_contact`: against the API they are
   * not in the row at all; on fixtures this is what keeps the demo persona
   * honest.
   */
  const visible = (entry: DirectoryEntry): DirectoryEntry =>
    entry.kind === "customer" && !canViewCustomerContact
      ? { ...entry, phone: undefined, email: undefined }
      : entry;

  // With the Type column showing, the rows wait for the type names too, so the
  // column never paints a moment of dashes before them. Only then: a list that
  // does not show types does not wait on a request it does not use.
  if (query.isPending || (showsTypes && typesQuery.isPending)) {
    return <TableSkeleton />;
  }

  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const filtered = countConditions(filter) > 0 || (search ?? "") !== "";
  const mayCreate = can(permissions, "contacts.contact.create");

  const drawerTitle = (): string => {
    if (opened === null) return "";
    const row = query.data.items.find((entry) => recordKey(entry) === recordId);
    const cold = opened.kind === "customer" ? openCustomer.data : undefined;
    return (
      [row?.displayName, cold?.displayName].find(
        (name) => name !== undefined && name.trim() !== "",
      ) ?? KIND_LABEL[opened.kind]
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/*
        Hidden without `contacts.contact.create` — a courtesy, not the
        enforcement (FR-ACL-8): `POST /api/contacts` calls
        `requirePermission()` regardless. Only a contact is ever created here;
        a customer comes from Shopify.
      */}
      {mayCreate ? (
        <>
          {/* In the page header beside Export, not a row of its own (#637); a floating button on a phone (#675). */}
          <SectionAction>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setCreating(true);
              }}
              className="max-md:hidden"
            >
              <Plus />
              New contact
            </Button>
          </SectionAction>
          <Fab
            label="New contact"
            icon={<Plus aria-hidden />}
            onClick={() => {
              setCreating(true);
            }}
          />
        </>
      ) : null}

      <ContactForm
        mode="create"
        open={creating}
        onOpenChange={setCreating}
        initialTypeIds={[]}
        onSaved={(contact) => {
          // Back to page one so a stale cursor doesn't point past the end once
          // the new row shifts everything after it; the drawer opens by id.
          setHistory([undefined]);
          open(recordKey({ kind: "contact", id: contact.id }));
        }}
      />

      {opened !== null ? (
        <RecordDrawer title={drawerTitle()}>
          {opened.kind === "customer" ? (
            canViewCustomers ? (
              <CustomerDetail customerId={opened.id} />
            ) : (
              <NoAccess permission="customers.view" />
            )
          ) : (
            <ContactDetail contactId={opened.id} />
          )}
        </RecordDrawer>
      ) : null}

      <DataTable
        fields={fields}
        rows={
          selection.viewing ? [...selection.rows.values()] : query.data.items
        }
        getRowId={recordKey}
        project={(entry) => projectDirectoryEntry(visible(entry))}
        selection={{
          isSelected: (entry) => selection.has(recordKey(entry)),
          onToggle: (entry, on) => {
            selection.set(entry, on);
          },
          onTogglePage: (entries, on) => {
            selection.setMany(entries, on);
          },
          labelOf: (entry) => `Select ${entry.displayName || "this contact"}`,
          active: selection.size > 0,
        }}
        renderOverride={(field, entry) => {
          if (field.name === "displayName") return <NameCell entry={entry} />;
          // The names of the types, not their ids (#514).
          if (field.name === "typeIds") {
            return renderCell(
              field,
              typeNamesOf(typesQuery.data, entry.typeIds),
            );
          }
          // A number on one line, with its Call button (#635).
          const phone = field.name === "phone" ? visible(entry).phone : null;
          return phone ? (
            <PhoneNumber
              number={phone}
              name={entry.displayName}
              className="flex-nowrap whitespace-nowrap"
            />
          ) : undefined;
        }}
        sort={sort}
        onToggleSort={onToggleSort}
        onRowClick={(entry) => {
          open(recordKey(entry));
        }}
        mobileRow={(entry) => <DirectoryMobileCard entry={visible(entry)} />}
        emptyState={
          filtered ? (
            <NoMatches onClear={onClearFilter} />
          ) : (
            <NothingYet
              title="No contacts yet"
              description={
                mayCreate
                  ? `Add a vendor, supplier or buyer with New contact.${
                      canViewCustomers
                        ? " Shopify customers appear here too, once they have been synced."
                        : ""
                    }`
                  : "Nobody has added a contact yet. Contacts are added by people who have permission to create them."
              }
              action={
                mayCreate ? (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      setCreating(true);
                    }}
                  >
                    <Plus />
                    New contact
                  </Button>
                ) : undefined
              }
            />
          )
        }
        footer={
          selection.viewing ? undefined : (
            <Pagination
              page={query.data}
              limit={limit}
              onLimitChange={(next) => {
                setLimit(next);
                setHistory([undefined]);
              }}
              history={history}
              onNavigate={setHistory}
            />
          )
        }
      />

      <SelectionBar
        selection={selection}
        {...selectionFacts({
          selection,
          pageRows: query.data.items,
          idOf: recordKey,
          inView: inViewOf(
            (entry: DirectoryEntry) => projectDirectoryEntry(entry),
            effective,
            reportingNow(),
          ),
        })}
        matching={query.data.total}
        onSelectAllMatching={async () => {
          const found: DirectoryEntry[] = [];
          let next: string | undefined;
          do {
            const page = await data.contacts.directory({
              filter: effective,
              search,
              sort,
              limit: 100,
              cursor: next,
            });
            found.push(...page.items);
            next = page.nextCursor ?? undefined;
          } while (next && found.length < SELECT_ALL_CAP);
          selection.setMany(found.slice(0, SELECT_ALL_CAP), true);
        }}
        noun="contacts"
        actions={
          <DirectoryBulkActions
            entries={[...selection.rows.values()]
              .slice(0, SELECT_ALL_CAP)
              .map(visible)}
            fields={fields}
          />
        }
      />
    </div>
  );
}
